import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { PaginationComponent } from '@shared/components/common/pagination/pagination.component'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { OptionsOrdersService } from '@modules/options-module/orders/services/options-orders.service'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { TranslatePipe } from '@ngx-translate/core'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { OptionsExecutedOrdersActionsComponent } from './common/options-executed-orders-actions/options-executed-orders-actions.component'
import { ColumnsOptionsExecutedOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/options/columns-options-executed-orders.enum'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { TuiSkeleton } from '@taiga-ui/kit'
import { TableComponent } from '@shared/components/common/table/table.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-options-executed-orders',
	templateUrl: './options-executed-orders.component.html',
	styleUrl: './options-executed-orders.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		PaginationComponent,
		TableColumnComponent,
		PricePipe,
		HighlightDirective,
		UpperCasePipe,
		DatePipe,
		TranslatePipe,
		QuantityPipe,
		ProfitPipe,
		TableActionsComponent,
		OptionsExecutedOrdersActionsComponent,
		TableFooterComponent,
		SecretDirective,
		TuiSkeleton,
		TableComponent,
	],
})
export class OptionsExecutedOrdersComponent implements OnInit, OnDestroy, OnChanges {
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum
	protected readonly columnsOptionsExecutedOrdersEnum: typeof ColumnsOptionsExecutedOrdersEnum =
		ColumnsOptionsExecutedOrdersEnum

	protected readonly pageSize: number = 30

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _optionsOrderService: OptionsOrdersService = inject<OptionsOrdersService>(OptionsOrdersService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	protected page: number = 1
	protected ordersResponse: WritableSignal<OrdersResponseInterface<OptionOrderInterface> | null> =
		signal<OrdersResponseInterface<OptionOrderInterface> | null>(null)
	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)
	protected ordersParams: WritableSignal<OrderParamsInterface<OptionOrderStateEnum> | undefined> = signal<
		OrderParamsInterface<OptionOrderStateEnum> | undefined
	>(undefined)
	protected needScrollToTop: WritableSignal<boolean> = signal<boolean>(false)
	protected statesColumns: WritableSignal<ColumnInterface<ColumnsOptionsExecutedOrdersEnum>[]> = signal<
		ColumnInterface<ColumnsOptionsExecutedOrdersEnum>[]
	>([])
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _launchOptionsOrders(): void {
		this._tradingLauncherService
			.launchOptionsOrders$(this.optionOrderStateEnum.FILLED)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				([ordersResponse, isLoading, ordersParams, isNeedUpdate]: [
					OrdersResponseInterface<OptionOrderInterface> | null,
					boolean,
					OrderParamsInterface<OptionOrderStateEnum> | undefined,
					boolean,
				]): void => {
					if (this.isLoading() !== isLoading) this.needScrollToTop.set(true)

					this.ordersResponse.set(ordersResponse)
					this.isLoading.set(isLoading)
					this.ordersParams.set(ordersParams)

					setTimeout((): void => this.needScrollToTop.set(false))
					if (isNeedUpdate) {
						this._optionsOrderHelperService.isNeedUpdateOptionsOrderResponseMap
							.get(this.optionOrderStateEnum.OPEN)
							?.next(false)
						this._getOrders()
					}
				},
			)
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.options.orders.filled.columns])
	}

	private _getOrders(): void {
		this._optionsOrderService
			.getOptionsOrders(this.optionOrderStateEnum.FILLED)
			.pipe(takeUntil(this._destroy$))
			.subscribe()
	}

	protected onChangePage(newPage: number): void {
		const ordersParams$: BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>> | undefined =
			this._optionsOrderHelperService.optionsOrdersParamsMap.get(this.optionOrderStateEnum.FILLED)
		if (!ordersParams$) return

		ordersParams$.next({ ...ordersParams$.getValue(), page: newPage })

		this._getOrders()
		this.isLoading.set(true)
	}

	protected getOptionSum(option: OptionOrderInterface): number {
		return option.qty * option.filled_avg_price * 100
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) this._updateStatesColumns()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._launchOptionsOrders()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
